import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";

type TestResultSpec = RouterOutputs["admin"]["testResultSpec"]["get"];

export function ResultOptionsTable({
  resultSpec,
}: {
  resultSpec: NonNullable<TestResultSpec>;
}) {
  const { data } = trpc.admin.testResultSpec.results.list.useQuery({
    testResultSpecId: resultSpec.id,
  });

  const { options } = useAdminGridOptions(
    data,
    [{ field: "description" }, { field: "value", headerName: "Code" }],
    { sideBar: undefined },
  );

  return (
    <TabLayout
      description="These results will be available for this spec during result entry."
      actions={
        <AddResultOptionDialog resultSpec={resultSpec} results={data || []} />
      }
    >
      <div className="flex h-96">
        <AdminGrid {...options} />
      </div>
    </TabLayout>
  );
}

const schema = z.object({
  testResultSpecId: z.number(),
  testResults: z.string().array(),
});
type FormValues = z.infer<typeof schema>;

function AddResultOptionDialog({
  resultSpec,
  results,
}: {
  resultSpec: { id: number; type: string };
  results: { value: string }[];
}) {
  const [open, setOpen] = useState(false);
  const allResults = trpc.admin.testResult.list.useQuery({ category: "TEXT" });
  const update = trpc.admin.testResultSpec.results.update.useMutation();

  const { control, handleSubmit, register } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    update.mutate(
      values,
      mutationResponse({
        errorTitle: "Error updating test results",
        successMessage: "Test results changed",
        onSuccess() {
          setOpen(false);
        },
      }),
    );
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button
        variant="secondary"
        isDisabled={resultSpec.type !== "TEXT"}
        onPress={() => setOpen(true)}
      >
        Edit test results
      </Button>
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Edit test results</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form onSubmit={handleSubmit(onSubmit)} id="add-result-form">
              <input
                type="hidden"
                {...register("testResultSpecId", { valueAsNumber: true })}
                value={resultSpec.id}
              />
              <Controller
                control={control}
                name="testResults"
                render={({ field }) => (
                  <GridList
                    items={allResults.data || []}
                    selectionMode="multiple"
                    defaultSelectedKeys={results.map((r) => r.value)}
                    aria-label="Test result options"
                    onSelectionChange={(keys) => {
                      field.onChange(Array.from(keys));
                    }}
                  >
                    {(result) => (
                      <GridListItem
                        key={result.value}
                        id={result.value}
                        textValue={result.description}
                      >
                        <Text>{result.description}</Text>
                      </GridListItem>
                    )}
                  </GridList>
                )}
              />
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              type="submit"
              form="add-result-form"
              isPending={update.isPending}
            >
              Submit
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
